/*
 * COPYRIGHT (c) 2022
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  formattedDate,
  getFirstDayofSelectedMonth,
  isObjectOrArrayAndEmpty
} from '../../../shared/Utils/Utils';
import { getConnctorConfig } from '../../../shared/Utils/DataConnectorUtils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import ICarbonEmissionHOCProps from './ICarbonEmissionHOCProps';
import useMounted from '../../../shared/useMount/useMount';
import CarbonGraphComponent from '../CarbonGraphComponent/CarbonGraphComponent';
import './CarbonEmissionHOC.scss';
import { IAny, ICarbonData, ISiteCard } from '../../IEnergyManagementProps';
import { getCarbonEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { ApiContext } from '../../../../context/ApiContext';
interface ICarbonEmissionHOCContent extends ISiteCard {
  data: ICarbonData;
  toggleFeatureScopeOne: boolean | undefined;
}

const DataAvailableCheck = (dataAvailableCheck: IAny) =>
  dataAvailableCheck ? true : false;

const CarbonEmissionGraphHOCContent = (props: ICarbonEmissionHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, toggleFeatureScopeOne } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <CarbonGraphComponent
      data={data}
      toggleFeatureScopeOne={toggleFeatureScopeOne}
    />
  );
};

const CarbonEmmisionGraphHOCWithLoader = withLoadingStatus(
  withErrorStatus(CarbonEmissionGraphHOCContent)
);

const getConnectorConfigName = (toggleFeatureScopeOne: boolean | undefined) => {
  return toggleFeatureScopeOne ? 'site-carbon-new' : 'Co2-carbonEmission';
};

const CarbonEmissionGraphHOC = (props: ICarbonEmissionHOCProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;
  const { bearerToken, requestContext } = props;
  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime = getCarbonEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const endTimeNewFormat = formattedDate(endTime);

  const connectorConfig = getConnctorConfig(
    getConnectorConfigName(toggleFeatureScopeOne),
    requestContext,
    startTime,
    endTimeNewFormat
  );

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      dataConnector?.actions?.getAll();
    }
  }, [requestContext]);

  const onRefresh = () => dataConnector?.actions?.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const CarbonEmissionSavings = (all as unknown) as ICarbonData;
  const isDataAvailable = DataAvailableCheck(CarbonEmissionSavings);

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(CarbonEmissionSavings);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <CarbonEmmisionGraphHOCWithLoader
        loading={isLoading}
        error={errorInfo}
        statusCode={statusCode}
        onRefresh={onRefresh}
        data={data}
        loadingText={t(d.LOADING)}
        title={t(d.CONNECTIONERRORTITLE)}
        noDataMessage={t(d.NODATAAVAILABLE)}
        connectionErrorMessage={t(d.CONNECTIONERROR)}
        toggleFeatureScopeOne={toggleFeatureScopeOne}
      />
    </>
  );
};
export default CarbonEmissionGraphHOC;
